import { escapeHtml } from '../../utils/printUtils';
import React, { useState, useEffect, useMemo } from 'react';
import { asService } from '../../services/asService';
import { partService } from '../../services/partService';
import { recallService } from '../../services/recallService';
import { vehicleService } from '../../services/vehicleService';

// 🚀 정확한 한국 로컬 날짜를 가져오는 함수
const getLocalDateString = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 🚀 특정 날짜가 포함된 주의 월요일~일요일 범위를 구하는 함수
const getWeekRange = (dateStr) => {
  const [y, m, d] = dateStr.split('-');
  const date = new Date(y, m - 1, d);
  const day = date.getDay();
  const diffToMonday = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(y, m - 1, diffToMonday);
  const sunday = new Date(y, m - 1, diffToMonday + 6);
  return {
    start: getLocalDateString(monday),
    end: getLocalDateString(sunday),
    label: `${getLocalDateString(monday)} ~ ${getLocalDateString(sunday)}`
  };
};

const ReportBuilder = () => {
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('전체'); 
  const [salesPeriod, setSalesPeriod] = useState('일일'); 
  
  // 🚀 [추가] 사용자가 선택한 기준 날짜 (기본값: 오늘)
  const [targetDate, setTargetDate] = useState(getLocalDateString());

  const [allAsRecords, setAllAsRecords] = useState([]); // 전체 완료된 AS 기록 저장
  const [stats, setStats] = useState({
    sales: { daily: 0, monthly: 0, yearly: 0 },
    asCount: { daily: 0, monthly: 0, yearly: 0 },
    parts: { totalTypes: 0, totalQty: 0, totalValue: 0, byModel: {}, momChange: 0 },
    allPartsRaw: [] 
  });

  const calculateStats = async () => {
    setLoading(true);
    try {
      const asRecordsAll = await asService.getRecords(); 
      const completedRecords = (asRecordsAll || []).filter(r => r.status === '완료');
      setAllAsRecords(completedRecords); // 원본 데이터 보관

      const allParts = await partService.getParts();

      const todayStr = getLocalDateString();
      const thisMonthStr = todayStr.substring(0, 7);
      const thisYearStr = todayStr.substring(0, 4);

      let dSales = 0, mSales = 0, ySales = 0;
      let dCount = 0, mCount = 0, yCount = 0;
      const usedPartsMap = {};

      completedRecords.forEach(r => {
        const costExVat = Math.round(((Number(r.totalCost) || 0) + (Number(r.discount) || 0)) / 1.1);
        const date = r.settleDate || r.asDate;
        if (!date) return;

        if (r.parts && Array.isArray(r.parts)) {
          r.parts.forEach(p => {
            usedPartsMap[p.taskName] = (usedPartsMap[p.taskName] || 0) + (Number(p.qty) || 0);
          });
        }

        if (date === todayStr) { dSales += costExVat; dCount++; }
        if (date.startsWith(thisMonthStr)) { mSales += costExVat; mCount++; }
        if (date.startsWith(thisYearStr)) { ySales += costExVat; yCount++; }
      });

      const modelMap = {};
      let pValue = 0, pQty = 0;
      const enrichedParts = (allParts || []).map(p => {
        const model = p.modelName || '공용모델';
        const currentStock = Number(p.stock) || 0;
        const usedQty = usedPartsMap[p.partName] || 0;
        const value = currentStock * (Number(p.purchasePrice) || 0);

        if (!modelMap[model]) modelMap[model] = { types: 0, qty: 0, value: 0 };
        modelMap[model].types += 1;
        modelMap[model].qty += currentStock;
        modelMap[model].value += value;

        pQty += currentStock;
        pValue += value;
        return { ...p, initialStock: currentStock + usedQty, usedQty, currentStock, value };
      });

      setStats({
        sales: { daily: dSales, monthly: mSales, yearly: ySales },
        asCount: { daily: dCount, monthly: mCount, yearly: yCount },
        parts: { totalTypes: allParts.length, totalQty: pQty, totalValue: pValue, byModel: modelMap },
        allPartsRaw: enrichedParts
      });
    } catch (error) { console.error("리포트 생성 실패:", error); }
    setLoading(false);
  };

  useEffect(() => { calculateStats(); }, []);

  // 🚀 [핵심 로직] 선택한 날짜/기간에 따른 상세 내역 필터링
  const periodDetailList = useMemo(() => {
    return allAsRecords.filter(r => {
      const date = r.settleDate || r.asDate;
      if (!date) return false;

      if (salesPeriod === '일일') {
        return date === targetDate;
      } else if (salesPeriod === '주간') {
        const range = getWeekRange(targetDate);
        return date >= range.start && date <= range.end;
      } else if (salesPeriod === '월간') {
        return date.startsWith(targetDate.substring(0, 7));
      }
      return false;
    }).map(r => ({
      ...r,
      costExVat: Math.round(((Number(r.totalCost) || 0) + (Number(r.discount) || 0)) / 1.1)
    }));
  }, [allAsRecords, salesPeriod, targetDate]);

  const periodTotal = useMemo(() => {
    return periodDetailList.reduce((sum, r) => sum + r.costExVat, 0);
  }, [periodDetailList]);

  const printReport = (title, tableHeaders, tableData, totalAmount) => {
    const printWindow = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            @page { size: A4; margin: 8mm; }
            body { font-family: 'Malgun Gothic', sans-serif; color: #1e293b; line-height: 1.3; }
            h1 { font-size: 16pt; border-bottom: 2px solid #334155; padding-bottom: 8px; text-align: center; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 10px; }
            th { background: #f1f5f9; padding: 7px; border: 1px solid #cbd5e1; font-size: 8pt; }
            td { padding: 5px; border: 1px solid #e2e8f0; font-size: 7.5pt; text-align: center; }
            .text-left { text-align: left; padding-left: 8px; }
            .text-right { text-align: right; padding-right: 8px; }
            .price-bold { font-weight: 900; }
            .total-section { margin-top: 15px; padding: 10px; border: 1px solid #cbd5e1; text-align: right; background: #f8fafc; }
            .total-value { font-size: 11pt; font-weight: bold; color: #2563eb; }
          </style>
        </head>
        <body>
          <div style="font-size: 7pt; text-align: right;">출력일: ${new Date().toLocaleString()}</div>
          <h1>${title}</h1>
          <table>
            <thead>
              <tr>
                <th style="width: 30px;">No</th>
                ${tableHeaders.map(h => `<th>${escapeHtml(h)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableData.map((row, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  ${row.map((cell, i) => {
                    let cls = "";
                    if (tableHeaders[i].includes("고객") || tableHeaders[i].includes("정보")) cls = "text-left";
                    if (tableHeaders[i].includes("금액") || tableHeaders[i].includes("가치")) cls = "text-right price-bold";
                    return `<td class="${cls}">${escapeHtml(cell)}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="total-section">합계 (VAT 제외): <span class="total-value">₩ ${totalAmount.toLocaleString()}</span></div>
          <script>setTimeout(() => { window.print(); window.close(); }, 500);</script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const styles = {
    wrapper: { padding: '30px', background: '#f4f7f9', minHeight: '100vh', fontFamily: 'sans-serif' },
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '30px' },
    card: { background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '30px' },
    label: { fontSize: '13px', color: '#64748b', fontWeight: 'bold', marginBottom: '12px' },
    value: { fontSize: '24px', fontWeight: '900', color: '#1e293b' },
    subValue: { fontSize: '13px', color: '#94a3b8', marginTop: '8px' },
    sectionTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '15px', overflow: 'hidden' },
    th: { background: '#f8fafc', padding: '12px 15px', textAlign: 'left', fontSize: '13px', color: '#64748b', borderBottom: '2px solid #e2e8f0' },
    td: { padding: '12px 15px', borderBottom: '1px solid #f1f5f9', fontSize: '14px' },
    btnPrint: { padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    input: { padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none' },
    btnFilter: (isActive) => ({
      padding: '6px 12px', fontSize: '12px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold',
      border: isActive ? 'none' : '1px solid #e2e8f0',
      background: isActive ? '#3b82f6' : 'white',
      color: isActive ? 'white' : '#64748b'
    })
  };

  return (
    <div style={styles.wrapper}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: '30px'}}>
        <h2 style={{margin:0}}>📊 E2B 통합 경영 분석 리포트</h2>
        <button onClick={calculateStats} disabled={loading} style={{padding:'10px 20px', borderRadius:'10px', border:'1px solid #e2e8f0', background: 'white', cursor:'pointer', fontWeight: 'bold'}}>
          {loading ? '데이터 동기화 중...' : '🔄 데이터 새로고침'}
        </button>
      </div>

      <div style={styles.summaryGrid}>
        <div style={styles.card}>
          <div style={styles.label}>💰 실시간 매출 요약 (VAT 제외)</div>
          <div><span style={{fontSize:'12px'}}>오늘:</span> <b style={styles.value}>₩{stats.sales.daily.toLocaleString()}</b></div>
          <div style={{marginTop:'12px'}}><span style={{fontSize:'12px'}}>이번달:</span> <b style={{fontSize: '18px'}}>₩{stats.sales.monthly.toLocaleString()}</b></div>
          <div style={styles.subValue}>올해 누적: ₩{stats.sales.yearly.toLocaleString()}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.label}>🛠️ AS 처리 건수 (SERVICE)</div>
          <div><span style={{fontSize:'12px'}}>오늘:</span> <b style={styles.value}>{stats.asCount.daily}건</b></div>
          <div style={{marginTop:'12px'}}><span style={{fontSize:'12px'}}>이번달:</span> <b style={{fontSize: '18px'}}>{stats.asCount.monthly}건</b></div>
          <div style={styles.subValue}>올해 합계: {stats.asCount.yearly}건</div>
        </div>
        <div style={styles.card}>
          <div style={styles.label}>📦 부품 자산 가치 (ASSETS)</div>
          <div style={styles.value}>₩{stats.parts.totalValue.toLocaleString()}</div>
          <div style={{marginTop:'10px', fontSize:'14px', color:'#10b981', fontWeight: 'bold'}}>총 {stats.parts.totalTypes}종 / {stats.parts.totalQty.toLocaleString()}개 보유</div>
          <div style={styles.subValue}>* 매입가 기준 재고 평가액</div>
        </div>
      </div>

      {/* 🚀 매출 상세 내역 및 리포트 출력 섹션 */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>
          <div style={{display:'flex', alignItems:'center', gap: '15px'}}>
            <span>📅 매출 상세 내역 조회</span>
            <div style={{display:'flex', gap: '5px'}}>
              <button style={styles.btnFilter(salesPeriod === '일일')} onClick={() => setSalesPeriod('일일')}>일일</button>
              <button style={styles.btnFilter(salesPeriod === '주간')} onClick={() => setSalesPeriod('주간')}>주간</button>
              <button style={styles.btnFilter(salesPeriod === '월간')} onClick={() => setSalesPeriod('월간')}>월간</button>
            </div>
            {/* 🚀 [날짜/월 선택기 추가] */}
            <input 
              type={salesPeriod === '월간' ? "month" : "date"} 
              style={styles.input} 
              value={salesPeriod === '월간' ? targetDate.substring(0, 7) : targetDate} 
              onChange={(e) => setTargetDate(e.target.value.length === 7 ? `${e.target.value}-01` : e.target.value)} 
            />
          </div>
          <button 
            onClick={() => printReport(
              `${salesPeriod} 매출 상세 내역서 (${salesPeriod === '주간' ? getWeekRange(targetDate).label : salesPeriod === '월간' ? targetDate.substring(0, 7) : targetDate})`,
              ['날짜', '고객명 (차량번호)', '담당 정비사', '매출금액 (공급가)'],
              periodDetailList.map(r => [r.settleDate || r.asDate, `${r.customerName} (${r.carNumber})`, r.mechanic, `₩ ${r.costExVat.toLocaleString()}`]),
              periodTotal
            )} 
            style={styles.btnPrint}
          >
            🖨️ 리포트 출력
          </button>
        </div>
        <table style={styles.table}>
          <thead>
            <tr><th style={styles.th}>날짜</th><th style={styles.th}>고객/차량</th><th style={styles.th}>정비사</th><th style={{...styles.th, textAlign:'right'}}>매출 금액</th></tr>
          </thead>
          <tbody>
            {periodDetailList.length > 0 ? periodDetailList.map((r, idx) => (
              <tr key={idx}>
                <td style={styles.td}>{r.settleDate || r.asDate}</td>
                <td style={styles.td}><b>{r.customerName}</b> ({r.carNumber})</td>
                <td style={styles.td}>{r.mechanic}</td>
                <td style={{...styles.td, textAlign:'right', fontWeight:'bold'}}>₩{r.costExVat.toLocaleString()}</td>
              </tr>
            )) : (
              <tr><td colSpan="4" style={{...styles.td, textAlign:'center', color:'#94a3b8', padding:'40px'}}>해당 기간에 완료된 매출 내역이 없습니다.</td></tr>
            )}
            <tr style={{background:'#f8fafc'}}>
              <td style={{...styles.td, fontWeight:'bold'}} colSpan="3">기간 합계 (Total Supply Value)</td>
              <td style={{...styles.td, textAlign:'right', fontWeight:'bold', color:'#2563eb', fontSize:'16px'}}>₩{periodTotal.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 부품 재고 상세 현황 (원본 유지) */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>
          <div style={{display:'flex', alignItems:'center', gap: '15px'}}>
            <span>🚘 부품 재고 상세 현황</span>
            <div style={{display:'flex', gap: '5px'}}>
              <button style={styles.btnFilter(selectedModel === '전체')} onClick={() => setSelectedModel('전체')}>전체</button>
              {Object.keys(stats.parts.byModel).map(model => (
                <button key={model} style={styles.btnFilter(selectedModel === model)} onClick={() => setSelectedModel(model)}>{model}</button>
              ))}
            </div>
          </div>
          <button onClick={() => printReport('부품 재고 현황 리포트', ['모델', '부품정보', '기초', '사용', '현재', '재고가치'], (selectedModel === '전체' ? stats.allPartsRaw : stats.allPartsRaw.filter(p => p.modelName === selectedModel)).map(p => [p.modelName, `${p.partNo}\n${p.partName}`, `${p.initialStock} EA`, `-${p.usedQty}`, `${p.currentStock} EA`, `₩ ${p.value.toLocaleString()}`]), (selectedModel === '전체' ? stats.allPartsRaw : stats.allPartsRaw.filter(p => p.modelName === selectedModel)).reduce((acc, cur) => acc + cur.value, 0))} style={{...styles.btnPrint, background:'#64748b'}}>🖨️ 재고 리포트 출력</button>
        </div>
        <table style={styles.table}>
          <thead>
            <tr><th style={styles.th}>모델</th><th style={styles.th}>부품명 / 코드</th><th style={{...styles.th, textAlign:'center'}}>기초</th><th style={{...styles.th, textAlign:'center'}}>사용</th><th style={{...styles.th, textAlign:'center'}}>현재고</th><th style={{...styles.th, textAlign:'right'}}>재고 평가액</th></tr>
          </thead>
          <tbody>
            {(selectedModel === '전체' ? stats.allPartsRaw : stats.allPartsRaw.filter(p => p.modelName === selectedModel)).slice(0, 20).map(p => (
              <tr key={p.id}>
                <td style={{...styles.td, color:'#64748b'}}>{p.modelName}</td>
                <td style={styles.td}><b>{p.partName}</b><br/><span style={{fontSize:'11px', color:'#94a3b8'}}>{p.partNo}</span></td>
                <td style={{...styles.td, textAlign:'center'}}>{p.initialStock}</td>
                <td style={{...styles.td, textAlign:'center', color:'#ef4444'}}>-{p.usedQty}</td>
                <td style={{...styles.td, textAlign:'center', fontWeight:'bold'}}>{p.currentStock}</td>
                <td style={{...styles.td, textAlign:'right', fontWeight:'bold'}}>₩{p.value.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportBuilder;